import { useEffect, useState } from "react";
import { useGlobalReducer } from "../../GlobalContext";
import { GetEntityAuditsByLoggingId } from "../api/entityService";
import { convertDayFormatForMoment, getAuditLabel } from "../utils/utility";
import { convertUTCDateTimeToLocal } from "../blocks/fields/formatDate";
import { SearchableSelect } from "../blocks/fields/SearchableSelect";
import { Loading } from "../elements/Loading";
import { Modal } from "../elements/Modal";
import { EntityAudit } from "./EntityAudit";
import { Audit, EntityAuditModalProps } from "./EntityInformation.types";

/** The audit with this logging id, or null until it arrives. */
function useAudit(label: string, loggingId: string) {
  const globalDispatch = useGlobalReducer();
  const [loaded, setLoaded] = useState<{ loggingId: string; audit: Audit } | null>(null);

  useEffect(() => {
    if (!loggingId) {
      return undefined;
    }

    let current = true;

    GetEntityAuditsByLoggingId(globalDispatch, label, loggingId)
      .then((response: { data?: Audit } | undefined) => {
        if (current && response?.data) {
          setLoaded({ loggingId, audit: response.data });
        }
      })
      .catch((error: unknown) => console.error("Error fetching audit:", error));

    return () => {
      current = false;
    };
  }, [globalDispatch, label, loggingId]);

  return loaded?.loggingId === loggingId ? loaded.audit : null;
}

const when = (audit: Audit) =>
  convertUTCDateTimeToLocal(audit.CreatedOn)?.format(convertDayFormatForMoment("dd/MM/yyyy HH:mm:ss"));

/** An audit in full, with another to compare it to beside it. */
export function EntityAuditModal({ label, audits, loggingId, onClose }: EntityAuditModalProps) {
  const [compareId, setCompareId] = useState("");
  const audit = useAudit(label, loggingId);
  const compareAudit = useAudit(label, compareId);

  if (!loggingId) {
    return null;
  }

  return (
    <Modal title="Audit" onClose={onClose} className="modal-fullscreen modal-dialog-scrollable">
      <div className="modal-body">
        {!audit ? (
          <Loading />
        ) : (
          <>
            <div className="row">
              <div className="col-6 border-end">
                <strong>{getAuditLabel(audit, false)}</strong>
                <p>{when(audit)}</p>
              </div>
              <div className="col-6">
                {audits.length > 1 && (
                  <>
                    <label htmlFor="compare-audit-to" className="form-label">
                      Compare to
                    </label>
                    <SearchableSelect
                      id="compare-audit-to"
                      clearable
                      placeholder="Please select"
                      value={compareId}
                      onChange={setCompareId}
                      options={audits
                        .filter((other) => other.LoggingId !== loggingId)
                        .map((other) => ({ value: other.LoggingId, label: getAuditLabel(other, true) }))}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-6 border-end">
                <EntityAudit audit={audit} />
              </div>
              <div className="col-6">{compareId && (compareAudit ? <EntityAudit audit={compareAudit} /> : <Loading />)}</div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default EntityAuditModal;
